<template>
    <!-- Dropzone, shown after the above button is selected: -->
    <div class="custom-image__dropzone brand-dropzone">
        <Dropzone
            id="myVueDropzone"
            class="dropzone"
            ref="dropzoneRef"
            :include-styling="false"
            :options="dzOptions"
            :use-custom-slot="true"
            v-on:vdropzone-file-added="imageAdded"
            v-on:vdropzone-removed-file="imageRemoved"
            v-on:vdropzone-sending="imageUploading"
            v-on:vdropzone-success="imageUploaded"
            v-on:vdropzone-error="imageUploadError"
        >
            <!-- Same treatment as the theme modalog's header-photo tile
                 (GH-893). The icon mounts as an <img> because this component
                 often renders twice per page (mobile band + desktop cell), and
                 an inline SVG gradient goes blank when the first copy of its
                 def sits in a display:none subtree. -->
            <span class="flex items-center gap-4 px-5 py-6 text-left">
                <img
                    :src="iconUploadPhotoUrl"
                    alt=""
                    class="h-8 w-[34px] shrink-0"
                    aria-hidden="true"
                />
                <span
                    class="block font-brand text-[14px] leading-[1.5] text-[#3c3938]"
                >
                    <strong>Drag &amp; Drop</strong> an image here or
                    <span class="font-bold underline underline-offset-2"
                        >Upload a Photo</span
                    >
                </span>
            </span>
        </Dropzone>
    </div>
</template>
<script setup>
import { ref } from "vue";
import { usePage } from "@inertiajs/vue3";
import Dropzone from "vue2-dropzone-vue3";
import iconUploadPhotoUrl from "~/svg/icon-upload-photo.svg?url";

//--- Emits
const emit = defineEmits([
    "image-added",
    "image-removed",
    "image-uploaded",
    "image-upload-error",
    "image-uploading",
]);

//--- Props
const props = defineProps({
    apiEndpoint: {
        type: String,
        default: "https://api.cloudinary.com/v1_1/gift-hero/image/upload",
        required: false,
    },
});

//--- State
const dropzoneRef = ref(null);
const dzFile = ref({});
const dzOptions = ref({
    acceptedFiles: ".jpg,.png,.jpeg,.gif",
    addRemoveLinks: true,
    autoProcessQueue: false,
    dictRemoveFile: "Remove Photo",
    dictFileTooBig: "Your image must be smaller than 8MB",
    headers: {
        "X-CSRF-Token": usePage().props.auth.token,
    },
    maxFiles: 1,
    maxFilesize: 8,
    thumbnailHeight: 192,
    thumbnailWidth: null,
    uploadMultiple: false,
    url: props.apiEndpoint,
});
const errors = ref(null); // If the API fails, this will become an object of key/value errors.
const fileUploading = ref(false);
const imageUploadVisible = ref(false);
const isLoading = ref(false);
const isValidPrice = ref(true);
const itemComments = ref("");
const transitionName = ref(null);
const useCustomDzOptions = ref(true);

//--- Methods
function imageAdded(file) {
    emit("image-added", file);
}

function imageRemoved(file, error, xhr) {
    emit("image-removed", file, error, xhr);
    dzFile.value = {};
}

function imageUploaded(file, response) {
    emit("image-uploaded", response.uuid);
    dzFile.value = file;
    fileUploading.value = false;
}

function imageUploadError(file, response) {
    emit("image-upload-error", file);
    // Custom error handling here.
    // https://gitlab.com/meno/dropzone/blob/master/src/dropzone.js#L811
    if (response.hasOwnProperty("file")) {
        for (let node of file.previewElement.querySelectorAll(
            "[data-dz-errormessage]",
        )) {
            node.textContent = response.file[0];
        }
    }

    console.log(file);
}

function imageUploading(file, xhr, formData) {
    fileUploading.value = true;
    emit("image-uploading", file, xhr, formData);
}

function removeAllFiles() {
    dropzoneRef.value.removeAllFiles(true);
}

function getQueuedFiles() {
    return dropzoneRef.value.getQueuedFiles();
}

defineExpose({
    getQueuedFiles,
    removeAllFiles,
});
</script>
<style>
/* Brand pass over the base dropzone.css the package imports globally. The
   .brand-dropzone wrapper out-specifies those single-class rules without
   touching them, so the treatment travels with this component everywhere
   it's mounted. */
/* Fill whatever height the caller gives the wrapper. The legacy
   custom-image__dropzone class pins children to flex-start, and on
   production builds app.scss links AFTER the extracted SFC CSS (see the
   Vite entry order in app.blade.php), so a bare .brand-dropzone rule ties
   on specificity and loses by source order — the compound selector wins
   on specificity in both environments (GH-1007). */
.custom-image__dropzone.brand-dropzone {
    align-items: stretch;
}

.brand-dropzone .dropzone {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 150px;
    padding: 12px;
    border: 1px dashed #d0cac8;
    border-radius: 5px;
    background-color: #fff;
    transition:
        border-color 0.15s ease,
        background-color 0.15s ease;
}

.brand-dropzone .dropzone.dz-drag-hover {
    border-color: #191e3e;
    border-style: dashed;
    background-color: rgba(236, 231, 227, 0.6);
}

.brand-dropzone .dropzone.dz-drag-hover .dz-message {
    opacity: 1;
}

/* Queued thumbnail preview */
.brand-dropzone .dropzone .dz-preview {
    margin: 4px;
    min-height: 0;
}

.brand-dropzone .dropzone .dz-preview .dz-image {
    overflow: hidden;
    border-radius: 5px;
}

.brand-dropzone .dropzone .dz-preview .dz-image img {
    display: block;
    margin: 0 auto;
}

/* The hover overlay (filename + size) and upload status marks read as
   debug chrome against the brand card — the thumbnail plus the remove
   link tell the whole story. */
.brand-dropzone .dropzone .dz-preview .dz-details,
.brand-dropzone .dropzone .dz-preview .dz-success-mark,
.brand-dropzone .dropzone .dz-preview .dz-error-mark {
    display: none;
}

.brand-dropzone .dropzone .dz-preview .dz-remove {
    margin-top: 10px;
    border: 0;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: #565a77;
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 0.15s ease;
}

.brand-dropzone .dropzone .dz-preview .dz-remove:hover {
    color: #f21439;
    text-decoration: underline;
}

.brand-dropzone .dropzone .dz-preview .dz-error-message {
    font-size: 13px;
    color: #f21439;
    text-align: center;
}
</style>
